import { useCallback, useRef, useEffect } from 'react';

import { useOutsideClick } from '@frontend/components';

import { useCascaderState } from '../context';
import { useMenuStack, usePreventDropdownMousedownFocus } from '../hooks';
import { InternalMenuWrapper } from '../subcomponents';
import type {
  CascaderOption,
  CascaderOptionGroup,
  CascaderSubmenu as CascaderSubmenuType,
} from '../types';
import { isSubmenu } from '../util';
import { CascaderSubmenu } from './CascaderSubmenu';

/**
 * CascaderMenu is responsible for organizing the cascading submenus of the Cascader,
 * deciding which submenu is the active menu, and rendering them in a stack according
 * to the cursor path.
 */
export const CascaderMenu = () => {
  /** Full dropdown surface for outside-click; must wrap any UI above the branch row (e.g. suggestion banners). */
  const menuSurfaceRef = useRef<HTMLDivElement>(null);
  /** Horizontal scroll container for the cascading submenu columns. */
  const menuScrollRef = useRef<HTMLDivElement>(null);
  const {
    getOptionsAtLevel,
    cursorPath,
    rootGroups,
    root,
    setCursorPath: onCursorPathChange,
    handleSelect: onSelect,
    onRequestClose,
    canSelectSubmenu,
  } = useCascaderState();
  const menuStack = useMenuStack(rootGroups, root);

  // Auto-scroll to the rightmost menu when a new submenu appears
  useEffect(() => {
    const el = menuScrollRef.current;
    if (el && menuStack.length > 0) {
      // Use a small delay to ensure the DOM has updated with the new submenu
      const timeoutId = setTimeout(() => {
        if (menuScrollRef.current) {
          menuScrollRef.current.scrollTo({
            left: menuScrollRef.current.scrollWidth,
            behavior: 'smooth',
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [menuStack.length]);

  // Helper function to build the full path for a selected option
  const buildFullPath = useCallback(
    (option: CascaderOption, level: number): string[] => {
      const fullPath: string[] = [];

      // Add values from the cursor path up to the selected level
      for (let i = 0; i < level; i++) {
        const currentOptions = getOptionsAtLevel(i);
        const currentIndex = cursorPath[i];
        if (currentIndex >= 0 && currentIndex < currentOptions.length) {
          const currentOption = currentOptions[currentIndex];
          fullPath.push(currentOption.value);
        }
      }

      // Add the selected option's value
      fullPath.push(option.value);

      return fullPath;
    },
    [cursorPath, getOptionsAtLevel],
  );

  // Handle option selection from the menu
  const handleSelectOption = useCallback(
    (option: CascaderOption, level: number) => {
      const currentOptions = getOptionsAtLevel(level);
      const optionIndex = currentOptions.findIndex(
        (opt) => opt.value === option.value,
      );

      if (!isSubmenu(option)) {
        const fullPath = buildFullPath(option, level);
        onSelect(option, fullPath);
        onRequestClose?.();
      } else {
        const newCursorPath = cursorPath.slice(0, level + 1);
        newCursorPath[level] = optionIndex;
        onCursorPathChange(newCursorPath);
      }
    },
    [
      cursorPath,
      buildFullPath,
      onSelect,
      onCursorPathChange,
      onRequestClose,
      getOptionsAtLevel,
    ],
  );

  const handleSubmenuCommit = useCallback(
    (option: CascaderOption, level: number) => {
      if (!canSelectSubmenu || !isSubmenu(option)) {
        return;
      }
      const fullPath = buildFullPath(option, level);
      onSelect(option, fullPath);
      onRequestClose?.();
    },
    [canSelectSubmenu, buildFullPath, onSelect, onRequestClose],
  );

  usePreventDropdownMousedownFocus(menuSurfaceRef);

  useOutsideClick({
    ref: menuSurfaceRef,
    handler: () => {
      onRequestClose?.();
    },
  });

  return (
    <InternalMenuWrapper menuRef={menuSurfaceRef} scrollRef={menuScrollRef}>
      {menuStack.map(({ groups, level, index, isOpen, submenu }) => (
        <CascaderSubmenu
          key={`${submenu.value}-${level}-${index}-${groups
            .flatMap((g) => g.children)
            .map((o) => o.value)
            .join('-')}`}
          submenu={submenu as CascaderSubmenuType<CascaderOption>}
          groups={groups as CascaderOptionGroup<CascaderOption>[]}
          level={level}
          cursorPath={cursorPath}
          isOpen={isOpen}
          onSelectOption={handleSelectOption}
          onSubmenuDoubleClick={
            canSelectSubmenu ? handleSubmenuCommit : undefined
          }
        />
      ))}
    </InternalMenuWrapper>
  );
};
